import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import NavbarIconsGenericComponent from '../generic-nav-bar/NavbarIconsGenericComponent';
import { startLoadingUserJourney, startSearchingJourneyUsers } from '../../app/actions/statisticActions';

const formatUserOption = (row) => {
  const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim();
  const userName = row.user_name ? `@${row.user_name}` : '';
  const email = row.email || '';
  return [fullName || userName || email, userName, email].filter(Boolean).join(' — ');
};

const ListStatisticsUserJourney = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const journey = useSelector((state) => state.statistic.userJourney);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);

  const loadJourney = (userId) => {
    if (!userId) return;
    dispatch(startLoadingUserJourney({ user_id: userId, from, to }));
  };

  const onSearch = async (e) => {
    if (e) e.preventDefault();
    const emailTrim = email.trim();
    const nameTrim = name.trim();
    if (!emailTrim && !nameTrim) {
      Swal.fire(
        t('titles.error'),
        t('semanticSearch.stats.journeyNeedQuery', {
          defaultValue: 'Indique email o nombre de usuario.',
        }),
        'warning'
      );
      return;
    }
    setSearching(true);
    const body = await dispatch(startSearchingJourneyUsers({
      email: emailTrim,
      name: nameTrim,
    }));
    setSearching(false);
    if (!body || !body.ok) return;
    const users = body.users || [];
    setCandidates(users);
    setTruncated(!!body.truncated);
    if (users.length === 0) {
      setSelectedUserId('');
      Swal.fire(
        t('semanticSearch.stats.journeyNoUsersTitle', { defaultValue: 'Sin coincidencias' }),
        t('semanticSearch.stats.journeyNoUsers', {
          defaultValue: 'No hay usuarios con ese email o nombre. Afine la búsqueda.',
        }),
        'info'
      );
      return;
    }
    if (users.length === 1) {
      setSelectedUserId(String(users[0].id));
      loadJourney(users[0].id);
      return;
    }
    setSelectedUserId('');
  };

  const onPickUser = (e) => {
    const nextId = e.target.value;
    setSelectedUserId(nextId);
    if (nextId) loadJourney(nextId);
  };

  const onReload = (e) => {
    if (e) e.preventDefault();
    if (selectedUserId) loadJourney(selectedUserId);
  };

  const user = journey?.user;
  const searches = journey?.searches || [];
  const results = journey?.results || [];
  const interests = journey?.interests || [];
  const supports = journey?.supports || [];
  const logins = journey?.logins || [];

  return (
    <>
      <NavbarIconsGenericComponent componentName="listStatistics" />
      <div className="container mt-4">
        <h2>{t('semanticSearch.stats.journeyTitle', { defaultValue: 'Recorrido por usuario' })}</h2>
        <p className="text-muted">
          {t('semanticSearch.stats.journeyHint', {
            defaultValue: 'Busque por email o nombre de usuario. Si hay varios, seleccione uno.',
          })}
        </p>
        <form className="row g-2 align-items-end mb-3" onSubmit={onSearch}>
          <div className="col-md-4">
            <label className="form-label" htmlFor="journey-email">
              {t('semanticSearch.stats.journeyEmail', { defaultValue: 'Email' })}
            </label>
            <input
              id="journey-email"
              type="text"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@dominio.org"
              autoComplete="off"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label" htmlFor="journey-name">
              {t('semanticSearch.stats.journeyUserName', { defaultValue: 'Nombre de usuario' })}
            </label>
            <input
              id="journey-name"
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('semanticSearch.stats.journeyUserNamePh', {
                defaultValue: 'user_name, nombre o apellido',
              })}
              autoComplete="off"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label" htmlFor="journey-from">
              {t('semanticSearch.admin.from', { defaultValue: 'Desde' })}
            </label>
            <input
              id="journey-from"
              type="date"
              className="form-control"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label" htmlFor="journey-to">
              {t('semanticSearch.admin.to', { defaultValue: 'Hasta' })}
            </label>
            <input
              id="journey-to"
              type="date"
              className="form-control"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="col-md-12 d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={searching}>
              {t('semanticSearch.stats.journeySearch', { defaultValue: 'Buscar usuario' })}
            </button>
            {selectedUserId && (
              <button type="button" className="btn btn-outline-secondary" onClick={onReload}>
                {t('semanticSearch.stats.journeyReload', { defaultValue: 'Cargar recorrido' })}
              </button>
            )}
          </div>
        </form>

        {candidates.length > 1 && (
          <div className="mb-4">
            <label className="form-label" htmlFor="journey-pick">
              {t('semanticSearch.stats.journeyPick', {
                defaultValue: 'Varios usuarios. Seleccione uno.',
              })}
              {' '}({candidates.length})
            </label>
            <select
              id="journey-pick"
              className="form-select"
              value={selectedUserId}
              onChange={onPickUser}
            >
              <option value="">
                {t('semanticSearch.stats.journeyPickPlaceholder', {
                  defaultValue: 'Seleccione un usuario…',
                })}
              </option>
              {candidates.map((row) => (
                <option key={row.id} value={row.id}>
                  {formatUserOption(row)} (id {row.id})
                </option>
              ))}
            </select>
            {truncated && (
              <p className="small text-muted mt-1">
                {t('semanticSearch.stats.journeyTruncated', {
                  defaultValue: 'Hay más de 25 coincidencias. Afine email o nombre.',
                })}
              </p>
            )}
          </div>
        )}

        {user && (
          <p>
            <strong>{user.first_name} {user.last_name}</strong>
            {user.user_name ? ` (@${user.user_name})` : ''} — {user.email} (id {user.id})
          </p>
        )}
        {journey?.summary && (
          <p className="small text-muted">
            {t('semanticSearch.stats.journeySummary', {
              defaultValue: 'Búsquedas {{searches}} · Resultados {{results}} · Intenciones {{interests}} · Soportes {{supports}} · Logins {{logins}}',
              searches: journey.summary.searches,
              results: journey.summary.results,
              interests: journey.summary.interests,
              supports: journey.summary.supports,
              logins: journey.summary.logins,
            })}
          </p>
        )}

        <h4>{t('semanticSearch.stats.journeySearches', { defaultValue: 'Búsquedas' })}</h4>
        <div className="table-responsive mb-3">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Id</th>
                <th>{t('semanticSearch.stats.col.search', { defaultValue: 'Texto' })}</th>
                <th>{t('semanticSearch.stats.col.module', { defaultValue: 'Módulo' })}</th>
                <th>{t('semanticSearch.admin.from', { defaultValue: 'Fecha' })}</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.text_search}</td>
                  <td>{row.catalogmodule_id}</td>
                  <td>{row.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4>{t('semanticSearch.stats.journeyInterests', { defaultValue: 'Intenciones' })}</h4>
        <div className="table-responsive mb-3">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>{t('semanticSearch.stats.col.event', { defaultValue: 'Evento' })}</th>
                <th>{t('semanticSearch.stats.col.module', { defaultValue: 'Módulo' })}</th>
                <th>{t('semanticSearch.stats.col.record', { defaultValue: 'Destino' })}</th>
                <th>{t('semanticSearch.stats.col.search', { defaultValue: 'Texto' })}</th>
                <th>{t('semanticSearch.admin.from', { defaultValue: 'Fecha' })}</th>
              </tr>
            </thead>
            <tbody>
              {interests.map((row) => (
                <tr key={row.id}>
                  <td>{row.event_type}</td>
                  <td>{row.module_key}</td>
                  <td>{row.target_reference_id}</td>
                  <td>{row.search_text}</td>
                  <td>{row.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4>{t('semanticSearch.stats.journeyPages', { defaultValue: 'Páginas de resultados' })}</h4>
        <div className="table-responsive mb-3">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>searchinfo_id</th>
                <th>{t('semanticSearch.stats.col.page', { defaultValue: 'Página' })}</th>
                <th>{t('semanticSearch.stats.col.items', { defaultValue: 'Items' })}</th>
                <th>{t('semanticSearch.admin.from', { defaultValue: 'Fecha' })}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={row.id}>
                  <td>{row.searchinfo_id}</td>
                  <td>{row.current_page}</td>
                  <td>{row.total_items}</td>
                  <td>{row.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4>{t('semanticSearch.stats.journeyLogins', { defaultValue: 'Ingresos (auditoría)' })}</h4>
        <div className="table-responsive mb-3">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>{t('semanticSearch.stats.col.outcome', { defaultValue: 'Resultado' })}</th>
                <th>IP</th>
                <th>{t('semanticSearch.stats.col.timezone', { defaultValue: 'Zona' })}</th>
                <th>{t('semanticSearch.stats.col.country', { defaultValue: 'País' })}</th>
                <th>{t('semanticSearch.admin.from', { defaultValue: 'Fecha' })}</th>
              </tr>
            </thead>
            <tbody>
              {logins.map((row) => (
                <tr key={row.id}>
                  <td>{row.outcome}</td>
                  <td>{row.ip}</td>
                  <td>{row.timezone}</td>
                  <td>{row.geo_country}</td>
                  <td>{row.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4>{t('semanticSearch.stats.journeySupports', { defaultValue: 'Soportes' })}</h4>
        <ul>
          {supports.map((row) => (
            <li key={row.id}>#{row.id} {row.module_key} ref {row.reference_id} — {row.status} — {row.channel}</li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default ListStatisticsUserJourney;
